// PrivacySettings Component
// Form for managing privacy settings

import React from 'react';
import { FaLock, FaGlobe, FaUsers, FaEye, FaInfoCircle } from 'react-icons/fa';
import styles from './PrivacySettings.module.css';

const PRIVACY_LEVELS = {
  public: {
    label: 'Public',
    icon: <FaGlobe />,
    description: 'Anyone can see this information',
    color: 'green'
  },
  clubs: {
    label: 'Clubs Only',
    icon: <FaUsers />,
    description: 'Only visible to members of clubs you\'ve been part of',
    color: 'blue'
  },
  private: {
    label: 'Private',
    icon: <FaLock />,
    description: 'Only you can see this information',
    color: 'red'
  }
};

const PRIVACY_SECTIONS = [
  {
    key: 'basicInfo',
    label: 'Basic Information',
    description: 'Name, headline, bio, location',
    recommended: 'public'
  },
  {
    key: 'experience',
    label: 'Experience',
    description: 'Professional sports experience, clubs, tournaments, achievements',
    recommended: 'public'
  },
  {
    key: 'athleticProfile',
    label: 'Athletic Profile',
    description: 'Sports, positions, physical attributes, performance metrics',
    recommended: 'public'
  },
  {
    key: 'awards',
    label: 'Awards & Achievements',
    description: 'Trophies, medals, recognitions',
    recommended: 'public'
  },
  {
    key: 'clubHistory',
    label: 'Club History',
    description: 'Teams and clubs you\'ve been part of',
    recommended: 'clubs'
  },
  {
    key: 'events',
    label: 'Event Participation',
    description: 'RSVP and attendance history',
    recommended: 'clubs'
  },
  {
    key: 'skills',
    label: 'Skills & Endorsements',
    description: 'Your skills and endorsements from others',
    recommended: 'public'
  },
  {
    key: 'media',
    label: 'Media Gallery',
    description: 'Videos, photos, and documents',
    recommended: 'public'
  },
  {
    key: 'availability',
    label: 'Availability & Preferences',
    description: 'Current status, location preferences, compensation',
    recommended: 'clubs'
  },
  {
    key: 'contact',
    label: 'Contact Information',
    description: 'Email, phone, preferred contact method',
    recommended: 'private'
  }
];

const PrivacySettings = ({ settings, onChange }) => {
  // Ensure settings exist
  if (!settings) {
    return <div>Loading form...</div>;
  }

  // Handle privacy level change
  const handlePrivacyChange = (section, level) => {
    onChange({
      ...settings,
      [section]: level
    });
  };

  // Detect which preset matches current settings
  const getActivePreset = () => {
    // Check if all sections are public
    const allPublic = PRIVACY_SECTIONS.every(section => 
      settings[section.key] === 'public'
    );
    if (allPublic) return 'open';

    // Check if all sections match recommended
    const allRecommended = PRIVACY_SECTIONS.every(section => 
      settings[section.key] === section.recommended
    );
    if (allRecommended) return 'balanced';

    // Check if all sections are private (except basicInfo = clubs)
    const allPrivate = PRIVACY_SECTIONS.every(section => 
      settings[section.key] === (section.key === 'basicInfo' ? 'clubs' : 'private')
    );
    if (allPrivate) return 'private';

    // Custom settings
    return null;
  };

  const activePreset = getActivePreset();

  // Apply privacy preset
  const applyPreset = (preset) => {
    let newSettings = {};
    
    switch (preset) {
      case 'open':
        // Everything public
        PRIVACY_SECTIONS.forEach(section => {
          newSettings[section.key] = 'public';
        });
        break;
      
      case 'balanced':
        // Use recommended settings
        PRIVACY_SECTIONS.forEach(section => {
          newSettings[section.key] = section.recommended;
        });
        break;
      
      case 'private':
        // Everything private except basic info
        PRIVACY_SECTIONS.forEach(section => {
          newSettings[section.key] = section.key === 'basicInfo' ? 'clubs' : 'private';
        });
        break;
      
      default:
        return;
    }
    
    onChange(newSettings);
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.header}>
        <h2 className={styles.sectionTitle}>
          <FaLock /> Privacy Settings
        </h2>
        <p className={styles.description}>
          Control who can see different parts of your profile. Choose carefully to balance 
          visibility for talent scouts with your privacy preferences.
        </p>
      </div>

      {/* Privacy Presets */}
      <div className={styles.presets}>
        <h3 className={styles.subsectionTitle}>Quick Presets</h3>
        <div className={styles.presetButtons}>
          <button
            type="button"
            className={`${styles.presetButton} ${activePreset === 'open' ? styles.active : ''}`}
            onClick={() => applyPreset('open')}
          >
            <FaGlobe />
            <span>Open Profile</span>
            <small>Maximum visibility</small>
          </button>
          <button
            type="button"
            className={`${styles.presetButton} ${activePreset === 'balanced' ? styles.active : ''} ${styles.recommended}`}
            onClick={() => applyPreset('balanced')}
          >
            <FaEye />
            <span>Balanced</span>
            <small>Recommended</small>
          </button>
          <button
            type="button"
            className={`${styles.presetButton} ${activePreset === 'private' ? styles.active : ''}`}
            onClick={() => applyPreset('private')}
          >
            <FaLock />
            <span>Private Profile</span>
            <small>Maximum privacy</small>
          </button>
        </div>
        {activePreset === null && (
          <p className={styles.customNote}>
            <FaInfoCircle /> You have custom privacy settings
          </p>
        )}
      </div>

      {/* Individual Section Settings */}
      <div className={styles.sections}>
        <h3 className={styles.subsectionTitle}>Section Privacy</h3>
        
        {PRIVACY_SECTIONS.map(section => (
          <div key={section.key} className={styles.privacySection}>
            <div className={styles.sectionInfo}>
              <h4 className={styles.sectionName}>{section.label}</h4>
              <p className={styles.sectionDescription}>{section.description}</p>
            </div>
            
            <div className={styles.privacyOptions}>
              {Object.entries(PRIVACY_LEVELS).map(([level, info]) => (
                <button
                  key={level}
                  type="button"
                  className={`${styles.privacyOption} ${settings[section.key] === level ? styles.active : ''}`}
                  onClick={() => handlePrivacyChange(section.key, level)}
                  aria-pressed={settings[section.key] === level}
                >
                  <span className={`${styles.optionIcon} ${styles[info.color]}`}>
                    {info.icon}
                  </span>
                  <span className={styles.optionLabel}>{info.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Privacy Info */}
      <div className={styles.infoBox}>
        <FaInfoCircle />
        <div>
          <h4>Understanding Privacy Levels</h4>
          <ul className={styles.infoList}>
            <li>
              <strong>Public:</strong> Visible to anyone visiting your profile, including 
              talent scouts and club representatives searching for players.
            </li>
            <li>
              <strong>Clubs Only:</strong> Visible only to verified members of clubs you've 
              been part of. Great for sensitive performance data.
            </li>
            <li>
              <strong>Private:</strong> Only you can see this information. Use for personal 
              notes or information you're not ready to share.
            </li>
          </ul>
        </div>
      </div>

      {/* Profile Visibility Stats */}
      <div className={styles.visibilityStats}>
        <h3 className={styles.subsectionTitle}>Profile Visibility Summary</h3>
        <div className={styles.statGrid}>
          <div className={styles.stat}>
            <FaGlobe className={styles.green} />
            <div>
              <span className={styles.statCount}>
                {Object.values(settings).filter(v => v === 'public').length}
              </span>
              <span className={styles.statLabel}>Public Sections</span>
            </div>
          </div>
          <div className={styles.stat}>
            <FaUsers className={styles.blue} />
            <div>
              <span className={styles.statCount}>
                {Object.values(settings).filter(v => v === 'clubs').length}
              </span>
              <span className={styles.statLabel}>Club-Only Sections</span>
            </div>
          </div>
          <div className={styles.stat}>
            <FaLock className={styles.red} />
            <div>
              <span className={styles.statCount}>
                {Object.values(settings).filter(v => v === 'private').length}
              </span>
              <span className={styles.statLabel}>Private Sections</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
